import React from 'react';
import Select, {
  SingleValue,
  components,
  OptionProps,
  SingleValueProps,
  GroupBase,
  Props as SelectProps,
  MenuListProps,
} from 'react-select';
import {
  useController,
  FieldValues,
  useFormContext,
  Path,
} from 'react-hook-form';
import FieldError from './FieldError';
import {
  DropdownIndicatorSingle,
  NullIndicatorSeparator,
} from '../../dropdown/ReactSelectComponents';
import { twMerge } from 'tailwind-merge';
import FieldLabel from './FieldLabel';
import FieldDescribe from './FieldDescribe';
import Spinner from '../../Spinner';

export type SelectOption = {
  value: string | number;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
};

type ReactSelectProps = Omit<
  SelectProps<SelectOption, false, GroupBase<SelectOption>>,
  'name' | 'options' | 'value' | 'onChange' | 'defaultValue'
>;

export interface FormSelectProps<TFieldValues extends FieldValues>
  extends ReactSelectProps {
  name: Path<TFieldValues>;
  label: string;
  options: SelectOption[];
  placeholder?: string;
  labelClassName?: string;
  isRequired?: boolean;
  withoutTouch?: boolean;
  describe?: React.ReactNode;
  callback?: () => void;
  onChange?: (newValue: SingleValue<SelectOption>) => void;
  disabled?: boolean;
}

const SingleValueSelect = (props: SingleValueProps<SelectOption>) => {
  const Icon = props.data?.icon;

  return (
    <components.SingleValue
      {...props}
      innerProps={{
        ...props.innerProps,
        className: 'text-sm font-medium !text-neutral-800',
      }}
    >
      <div className="flex-row flex items-center gap-2.5">
        {Icon && <Icon className="w-5 shrink-0" />}
        <span>{props.children}</span>
      </div>
    </components.SingleValue>
  );
};

const Option = (props: OptionProps<SelectOption>) => {
  const optionClasses = twMerge(
    '!text-sm !text-neutral-800 p-2',
    props.isFocused && 'bg-gray-100',
    props.isSelected && 'bg-clc-blue !text-white',
    !props.isSelected && props.isDisabled && 'opacity-50 cursor-not-allowed',
  );

  const Icon = props.data?.icon;

  return (
    <components.Option {...props} className={optionClasses}>
      <div className="flex-row flex items-center gap-2.5">
        {Icon && <Icon className="w-5 shrink-0" />}
        <span>{props.children}</span>
      </div>
    </components.Option>
  );
};

const CustomMenuList = (props: MenuListProps<SelectOption>) => {
  const { isLoading } = props.selectProps;

  return (
    <components.MenuList {...props}>
      {props.children}

      {isLoading && (
        <div className="flex justify-center items-center py-1.5">
          <Spinner />
        </div>
      )}
    </components.MenuList>
  );
};

const FormSelect = <TFieldValues extends FieldValues>({
  name,
  label,
  options,
  placeholder,
  isRequired,
  callback,
  describe,
  className,
  isLoading,
  withoutTouch = true,
  labelClassName,
  onChange,
  ...props
}: FormSelectProps<TFieldValues>) => {
  const { control } = useFormContext<TFieldValues>();
  const {
    field,
    fieldState: { error, isTouched },
  } = useController({ name, control });

  const touch = withoutTouch || isTouched;

  const selectedValue = options.find((option) => option.value === field.value);
  const handleChange = (newValue: SingleValue<SelectOption>) => {
    field.onChange(newValue ? newValue.value : null);
    callback && callback();
  };

  return (
    <div className={twMerge('flex w-full flex-col gap-0.5', className)}>
      <FieldLabel
        name={name}
        className={labelClassName}
        label={label}
        isRequired={isRequired}
      />
      <Select
        {...field}
        {...props}
        id={name}
        isDisabled={props.disabled}
        key={`${name}-${field.value}`}
        isSearchable
        options={options}
        placeholder={placeholder}
        value={selectedValue}
        isMulti={false}
        isLoading={isLoading}
        onChange={onChange || handleChange}
        maxMenuHeight={140}
        onBlur={field.onBlur}
        components={{
          DropdownIndicator: DropdownIndicatorSingle,
          IndicatorSeparator: NullIndicatorSeparator,
          Option,
          SingleValue: SingleValueSelect,
          MenuList: CustomMenuList,
        }}
        className="dropdown"
        classNamePrefix={
          error && touch && !props.disabled
            ? 'react-select-with-error'
            : 'react-select'
        }
      />
      {error && touch && !props.disabled && (
        <FieldError>{error.message}</FieldError>
      )}
      {!error && describe && <FieldDescribe>{describe}</FieldDescribe>}
    </div>
  );
};

export default FormSelect;
